





































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { TriggeredAction } from '@/api/model/triggered-action'
import { cloneDeep, isEqual } from 'lodash'
import {
    ActionAssignToType,
    ActionType,
    availableActionAppointmentTypes,
    availableActionAssignToTypes,
    availableActionTypes,
    availableDelayUnits,
    TriggerType,
} from '@/api/types'
import { Pipeline } from '@/api/model/pipeline'
import { PipelineAPI } from '@/api/backend-client'
import { InputSelectOption } from '@/helpers/options'
import { Branch } from '@/api/model/branch'
import { User } from '@/api/model/user'

@Component
export default class CreateAutoActionFormStep3 extends Vue {
    @Prop({ required: true }) readonly value!: TriggeredAction
    editingValue: TriggeredAction = null
    triggerType: string = null
    loadingPipelines: boolean = true
    referralPipelines: Pipeline[] = []

    @Watch('value', { immediate: true })
    onValueChanged() {
        this.editingValue = cloneDeep(this.value)
        if (!this.triggerType) {
            this.triggerType = this.editingValue.triggerType
        }
    }

    @Watch('editingValue.actionType')
    @Watch('editingValue.actionAppointmentType')
    @Watch('editingValue.delayUnit')
    @Watch('editingValue.delayQuantity')
    @Watch('editingValue.newReferralPipeline')
    @Watch('editingValue.phase')
    @Watch('editingValue.assignedToBranch')
    @Watch('editingValue.assignedTo')
    @Watch('editingValue.actionAssignToType')
    @Watch('editingValue.actionAssignTo')
    onEditingValueChanged() {
        if (isEqual(this.editingValue, this.value)) {
            return
        }

        this.$emit('update:value', this.editingValue)
    }

    mounted() {
        this.loadPipelines()
    }

    async loadPipelines(): Promise<void> {
        this.loadingPipelines = true
        this.referralPipelines = await PipelineAPI.getAll({ company: this.$currentCompany.id })
        this.loadingPipelines = false
    }

    get availableActionTypeOptions() {
        return availableActionTypes()
            .filter(value => value !== ActionType.MEETING)
            .map(actionType => ({
                label: this.$t('action.type.' + actionType),
                value: actionType,
            }))
    }

    get isMeeting(): boolean {
        return this.editingValue.actionType === ActionType.MEETING
    }

    get isAddToPipeline(): boolean {
        return this.editingValue.actionType === ActionType.ADD_TO_PIPELINE
    }

    get availableActionAppointmentTypeOptions() {
        return availableActionAppointmentTypes().map(actionAppointmentOption => ({
            label: this.$t('action.appointmentType.' + actionAppointmentOption),
            value: actionAppointmentOption,
        }))
    }

    get availableDelayUnitOptions() {
        return availableDelayUnits().map(unit => ({
            label: this.$t('forms.triggered_action_form.delay_unit.' + unit),
            value: unit,
        }))
    }

    get availableNewReferralPipelines(): Pipeline[] {
        return this.referralPipelines
            .filter(pipeline => pipeline.active)
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    get phase(): string {
        return this.editingValue?.phase?.id
    }

    set phase(id: string|null) {
        this.editingValue.phase = this.editingValue.newReferralPipeline.activePhases
            .find(pipelinePhase => pipelinePhase.id === id)
    }

    get availablePhases(): InputSelectOption[] {
        if (!this.editingValue.newReferralPipeline) {
            return []
        }

        return this.editingValue.newReferralPipeline.activePhases.map(phase => ({ label: phase.name, value: phase.id }))
    }

    get availableAssignedToBranches(): Branch[] {
        return this.$currentCompany.getAvailableBranches()
    }

    get availableAssignedTo(): User[] {
        return this.$currentCompany.getAvailableUsers({
            branch: this.editingValue.assignedToBranch,
        })
    }

    get availableUserOptions() {
        return this.$currentCompany.getAvailableUsers()
    }

    get availableActionAssignedToTypeOptions() {
        let options = availableActionAssignToTypes().map(actionAssignToType => ({
            label: this.$t('forms.triggered_action_form.action-assigned-to-type-options.' + actionAssignToType),
            value: actionAssignToType,
        }))

        options.push({
            label: this.$t('forms.triggered_action_form.action-assigned-to-type-options.specific'),
            value: null,
        })

        if (this.editingValue.triggerType !== TriggerType.appointment) {
            options = options.filter(option => option.value !== ActionAssignToType.actionAssignedTo)
        }

        return options
    }

    get statusTranslation() {
        return this.$t(`referral.phaseModelStatus.${this.value.changedFieldValue}`)
    }

    get phaseTranslation() {
        const phaseChangedFieldValue = this.value.referralPipeline.phases
            .find(phase => phase.id  === this.value.changedFieldValue)

        return phaseChangedFieldValue?.name
    }

    get avatarHeaderText() {
        if (this.value.changedField === null) {
            return this.$t('virtualColleague.autoAction.avatarHeader.stepThreeAppointment')
        } else if (this.value.changedField === 'status') {
            return this.$t('virtualColleague.autoAction.avatarHeader.stepThreeChange', {
                changedField: 'de status',
                changedFieldValue: this.statusTranslation,
            })
        } else {
            return this.$t('virtualColleague.autoAction.avatarHeader.stepThreeChange', {
                changedField: 'de fase',
                changedFieldValue: this.phaseTranslation,
            })
        }
    }
}
